import React, { useMemo, useState } from 'react';
import { UserRole, ProfileType, DateFilter, Commission, ApprovalStatus } from '../types/commission';
import { FilterBar } from './FilterBar';
import { SearchBar } from './SearchBar';
import { RoleSwitcher } from './RoleSwitcher';
import { InactivityTimer } from './InactivityTimer';
import { LoanOfficerView } from './LoanOfficerView';
import { AdminView } from './AdminView';
import { AdminSummaryMetrics } from './AdminSummaryMetrics';
import { FlaggedCommissionsPanel } from './FlaggedCommissionsPanel';
import { PinEntry } from './PinEntry';
import { ComingSoonView } from './ComingSoonView';
import { LeaderboardView } from './LeaderboardView';
import { RZoneView } from './RZoneView';
import { PipelineView } from './PipelineView';
import { PullThroughView } from './PullThroughView';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { Home, Key, DollarSign, FileText, Handshake, Building2, Percent, Shield, Award, TrendingUp, Landmark, PiggyBank, Banknote, Gift, Lock, Zap, BarChart3, GitBranch, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
const CURRENT_USER_ID = 'u1';
const INACTIVITY_TIMEOUT = 30000;
// Floating background icons configuration
const FLOATING_ICONS = [{
  Icon: Home,
  x: 5,
  y: 15,
  size: 28,
  delay: 0,
  duration: 18,
  className: 'float-icon-1'
}, {
  Icon: Key,
  x: 88,
  y: 25,
  size: 22,
  delay: 2,
  duration: 22,
  className: 'float-icon-2'
}, {
  Icon: DollarSign,
  x: 15,
  y: 55,
  size: 32,
  delay: 4,
  duration: 16,
  className: 'float-icon-3'
}, {
  Icon: FileText,
  x: 75,
  y: 65,
  size: 24,
  delay: 1,
  duration: 20,
  className: 'float-icon-1'
}, {
  Icon: Handshake,
  x: 45,
  y: 85,
  size: 26,
  delay: 3,
  duration: 19,
  className: 'float-icon-2'
}, {
  Icon: Building2,
  x: 92,
  y: 45,
  size: 30,
  delay: 5,
  duration: 17,
  className: 'float-icon-3'
}, {
  Icon: Percent,
  x: 30,
  y: 10,
  size: 20,
  delay: 2.5,
  duration: 21,
  className: 'float-icon-1'
}, {
  Icon: Shield,
  x: 60,
  y: 35,
  size: 22,
  delay: 6,
  duration: 23,
  className: 'float-icon-2'
}, {
  Icon: Award,
  x: 8,
  y: 80,
  size: 26,
  delay: 1.5,
  duration: 18,
  className: 'float-icon-3'
}, {
  Icon: TrendingUp,
  x: 50,
  y: 50,
  size: 24,
  delay: 3.5,
  duration: 20,
  className: 'float-icon-1'
}, {
  Icon: Landmark,
  x: 70,
  y: 8,
  size: 28,
  delay: 4.5,
  duration: 22,
  className: 'float-icon-2'
}, {
  Icon: PiggyBank,
  x: 25,
  y: 38,
  size: 22,
  delay: 0.5,
  duration: 19,
  className: 'float-icon-3'
}, {
  Icon: Home,
  x: 82,
  y: 78,
  size: 20,
  delay: 7,
  duration: 21,
  className: 'float-icon-1'
}, {
  Icon: DollarSign,
  x: 40,
  y: 20,
  size: 18,
  delay: 2,
  duration: 24,
  className: 'float-icon-2'
}, {
  Icon: Key,
  x: 55,
  y: 70,
  size: 24,
  delay: 5.5,
  duration: 17,
  className: 'float-icon-3'
}];
function FloatingBackgroundIcons() {
  return <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{
    zIndex: 0
  }} data-id="element-127">
      {FLOATING_ICONS.map((item, i) => {
      const {
        Icon,
        x,
        y,
        size,
        delay,
        duration,
        className
      } = item;
      return <div key={i} className={`absolute ${className}`} style={{
        left: `${x}%`,
        top: `${y}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      }} data-id="element-128">
            <Icon size={size} className="text-white" style={{
          opacity: 0.07
        }} strokeWidth={1.5} data-id="element-129" />
          </div>;
    })}
    </div>;
}
type MainTab = 'rzone' | 'pipeline' | 'pullthrough' | 'leaderboard' | 'commission' | 'rewards';
// === REWARD CENTER VIEW ===
function RewardCenterView() {
  return <div className="max-w-2xl mx-auto p-6" data-id="element-130">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center py-20" data-id="element-131">
        <motion.div animate={{
        y: [0, -8, 0],
        rotate: [0, 3, -3, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20 mb-6" data-id="element-132">
          <Gift size={48} className="text-purple-400" data-id="element-133" />
        </motion.div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4" data-id="element-134">
          <Lock size={12} className="text-purple-400" data-id="element-135" />
          <span className="text-xs font-bold text-purple-400 uppercase tracking-wider" data-id="element-136">
            Coming Soon
          </span>
        </div>
        <h2 className="text-3xl font-bold text-white mb-3" data-id="element-137">Reward Center</h2>
        <p className="text-slate-400 text-sm max-w-md mx-auto leading-relaxed" data-id="element-138">
          Earn points for closing loans, hitting tier milestones, and
          maintaining streaks. Redeem them for exclusive rewards, bonuses, and
          perks.
        </p>
        <div className="mt-8 flex items-center justify-center gap-4" data-id="element-139">
          <div className="px-4 py-3 rounded-xl bg-navy-800/70 border border-white/5" data-id="element-140">
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-141">
              Points System
            </p>
            <p className="text-sm font-bold text-accent-400" data-id="element-142">
              🪙 Earn & Redeem
            </p>
          </div>
          <div className="px-4 py-3 rounded-xl bg-navy-800/70 border border-white/5" data-id="element-143">
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-144">
              Streaks
            </p>
            <p className="text-sm font-bold text-orange-400" data-id="element-145">
              🔥 Daily Bonuses
            </p>
          </div>
          <div className="px-4 py-3 rounded-xl bg-navy-800/70 border border-white/5" data-id="element-146">
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-147">
              Prizes
            </p>
            <p className="text-sm font-bold text-emerald-400" data-id="element-148">
              🎁 Exclusive Perks
            </p>
          </div>
        </div>
      </motion.div>
    </div>;
}
interface CommissionDashboardProps {
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  commissions: Commission[];
  onUpdateCommissions: (commissions: Commission[]) => void;
}
export function CommissionDashboard({
  currentRole,
  onRoleChange,
  commissions,
  onUpdateCommissions
}: CommissionDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileType[]>(['Loan Officer', 'Sales Team Leader', 'Division Leader']);
  const [dateFilter, setDateFilter] = useState<DateFilter>('This Month');
  const [showFlaggedPanel, setShowFlaggedPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<MainTab>('rzone');
  // Admin "View as User" state
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  // Commission Center auth - PIN lock only for this tab
  const [isCommissionAuthed, setIsCommissionAuthed] = useState(false);
  const handleCommissionTimeout = () => {
    setIsCommissionAuthed(false);
  };
  const {
    remainingTime
  } = useInactivityTimer({
    timeout: INACTIVITY_TIMEOUT,
    onTimeout: handleCommissionTimeout
  });
  const isLoanOfficer = currentRole === 'Loan Officer';
  // When switching to commission tab and not authed, stay on tab but show lock
  const showCommissionLock = activeTab === 'commission' && !isCommissionAuthed;
  // Filter commissions based on role and filters
  const filteredCommissions = useMemo(() => {
    let result = [...commissions];
    if (isLoanOfficer) {
      result = result.filter(item => item.userId === CURRENT_USER_ID);
    } else {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        result = result.filter(item => item.userName.toLowerCase().includes(query) || item.userProfile.toLowerCase().includes(query));
      }
      if (selectedProfiles.length > 0) {
        result = result.filter(item => selectedProfiles.includes(item.userProfile));
      }
    }
    return result;
  }, [commissions, isLoanOfficer, searchQuery, selectedProfiles]);
  const updateCommissionStatus = (commission: Commission): Commission => {
    const loans = commission.loans;
    let newStatus: ApprovalStatus = 'pending';
    const hasFlagged = loans.some(l => l.approvalStatus === 'flagged');
    const hasUnderReview = loans.some(l => l.approvalStatus === 'under_review');
    const hasResolvedPending = loans.some(l => l.approvalStatus === 'resolved_pending_approval');
    const allApproved = loans.length > 0 && loans.every(l => l.approvalStatus === 'approved');
    if (hasUnderReview) newStatus = 'under_review';else if (hasResolvedPending) newStatus = 'resolved_pending_approval';else if (hasFlagged) newStatus = 'flagged';else if (allApproved) newStatus = 'approved';else newStatus = 'pending';
    const flaggedLoanIds = loans.filter(l => ['flagged', 'under_review', 'resolved_pending_approval'].includes(l.approvalStatus)).map(l => l.loanId);
    return {
      ...commission,
      review: {
        ...commission.review!,
        status: newStatus,
        flaggedLoans: flaggedLoanIds,
        approvedAt: newStatus === 'approved' ? new Date().toISOString() : commission.review?.approvedAt,
        approvedBy: newStatus === 'approved' ? commission.userName : commission.review?.approvedBy
      }
    };
  };
  const handleApproveLoan = (userId: string, loanId: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => loan.loanId === loanId ? {
        ...loan,
        approvalStatus: 'approved' as ApprovalStatus
      } : loan);
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans
      });
    }));
  };
  const handleApproveAllLoans = (userId: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => loan.approvalStatus === 'pending' ? {
        ...loan,
        approvalStatus: 'approved' as ApprovalStatus
      } : loan);
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans
      });
    }));
  };
  const handleFlagLoan = (userId: string, loanId: string, comment: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => loan.loanId === loanId ? {
        ...loan,
        approvalStatus: 'flagged' as ApprovalStatus,
        flagComment: comment,
        loanReviewComments: [...(loan.loanReviewComments || []), {
          id: Date.now().toString(),
          author: c.userName,
          authorRole: 'Loan Officer',
          message: comment,
          timestamp: new Date().toLocaleTimeString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })
        }]
      } : loan);
      const newReviewComment = {
        id: Date.now().toString(),
        author: c.userName,
        authorRole: 'Loan Officer' as UserRole,
        message: `[${loanId}] ${comment}`,
        timestamp: new Date().toLocaleTimeString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans,
        review: {
          ...c.review!,
          comments: [...(c.review?.comments || []), newReviewComment]
        }
      });
    }));
  };
  const handleSubmitLoanResponse = (userId: string, loanId: string, message: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => loan.loanId === loanId ? {
        ...loan,
        loanReviewComments: [...(loan.loanReviewComments || []), {
          id: Date.now().toString(),
          author: c.userName,
          authorRole: 'Loan Officer',
          message,
          timestamp: new Date().toLocaleTimeString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })
        }]
      } : loan);
      const newReviewComment = {
        id: Date.now().toString(),
        author: c.userName,
        authorRole: 'Loan Officer' as UserRole,
        message: `[${loanId}] ${message}`,
        timestamp: new Date().toLocaleTimeString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans,
        review: {
          ...c.review!,
          comments: [...(c.review?.comments || []), newReviewComment]
        }
      });
    }));
  };
  const handleApproveLoanResolution = (userId: string, loanId: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => {
        if (loan.loanId !== loanId) return loan;
        const finalCommission = loan.overriddenCommission ?? loan.commission;
        return {
          ...loan,
          approvalStatus: 'approved' as ApprovalStatus,
          commission: finalCommission,
          overriddenCommission: undefined
        };
      });
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans
      });
    }));
  };
  const handleSubmitCounter = (userId: string, loanId: string, message: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => loan.loanId === loanId ? {
        ...loan,
        approvalStatus: 'under_review' as ApprovalStatus,
        loanReviewComments: [...(loan.loanReviewComments || []), {
          id: Date.now().toString(),
          author: 'Admin',
          authorRole: 'System Administrator',
          message,
          timestamp: new Date().toLocaleTimeString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })
        }]
      } : loan);
      const newReviewComment = {
        id: Date.now().toString(),
        author: 'Admin',
        authorRole: 'System Administrator' as UserRole,
        message: `[${loanId}] ${message}`,
        timestamp: new Date().toLocaleTimeString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans,
        review: {
          ...c.review!,
          comments: [...(c.review?.comments || []), newReviewComment]
        }
      });
    }));
  };
  const handleSubmitResolution = (userId: string, overrides: {
    loanId: string;
    newCommission: number;
  }[], message: string) => {
    onUpdateCommissions(commissions.map(c => {
      if (c.userId !== userId) return c;
      const updatedLoans = c.loans.map(loan => {
        const override = overrides.find(o => o.loanId === loan.loanId);
        if (override) {
          return {
            ...loan,
            overriddenCommission: override.newCommission,
            approvalStatus: 'resolved_pending_approval' as ApprovalStatus,
            loanReviewComments: [...(loan.loanReviewComments || []), {
              id: Date.now().toString(),
              author: 'Admin',
              authorRole: 'System Administrator',
              message: `Resolution proposed: Commission adjusted to $${override.newCommission}`,
              timestamp: new Date().toLocaleTimeString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })
            }]
          };
        }
        return loan;
      });
      const newTotal = updatedLoans.reduce((acc, loan) => acc + (loan.overriddenCommission ?? loan.commission), 0);
      const newReviewComment = {
        id: Date.now().toString(),
        author: 'Admin',
        authorRole: 'System Administrator' as UserRole,
        message,
        timestamp: new Date().toLocaleTimeString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      return updateCommissionStatus({
        ...c,
        loans: updatedLoans,
        review: {
          ...c.review!,
          overriddenTotal: newTotal,
          comments: [...(c.review?.comments || []), newReviewComment]
        }
      });
    }));
  };
  return <div className="flex flex-col h-screen overflow-hidden font-sans text-slate-900 gamified-bg" data-id="element-149">
      <FloatingBackgroundIcons data-id="element-150" />

      {/* Top Header */}
      <header className="bg-gradient-to-r from-navy-900/95 via-navy-800/90 to-navy-900/95 backdrop-blur-xl border-b border-white/10 px-8 py-4 flex justify-between items-center shrink-0 z-20 relative header-glow-border" data-id="element-151">
        <div className="flex items-center gap-4" data-id="element-152">
          <img src="https://cdn.magicpatterns.com/uploads/8NoVCvBe7DuNgWECKxstsS/R_Platform_Logo.png" alt="R Platform" className="h-10 w-auto object-contain brightness-0 invert" data-id="element-153" />
          <div className="flex items-baseline gap-0.5" data-id="element-154">
            {'PLATFORM'.split('').map((letter, i) => <motion.span key={i} initial={{
            opacity: 0,
            y: -10,
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} transition={{
            delay: 0.3 + i * 0.07,
            duration: 0.4,
            ease: 'easeOut'
          }} className="text-xl font-bold gradient-text" style={{
            display: 'inline-block'
          }} data-id="element-155">
                {letter}
              </motion.span>)}
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-156">
          {/* Only show role switcher & timer when on Commission Center and authed */}
          {activeTab === 'commission' && isCommissionAuthed && <>
              <div className="flex items-center gap-3" data-id="element-157">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-158">
                  Viewing as:
                </div>
                <RoleSwitcher currentRole={currentRole} onRoleChange={onRoleChange} data-id="element-159" />
              </div>
              <div className="h-6 w-px bg-white/10" data-id="element-160" />
              <InactivityTimer remainingTime={remainingTime} data-id="element-161" />
            </>}
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-navy-900/40 backdrop-blur-xl border-b border-white/10 px-8 shrink-0 z-10 relative" data-id="element-162">
        <div className="flex items-center gap-1" data-id="element-163">
          {[{
          key: 'rzone' as MainTab,
          label: 'R Zone',
          icon: Zap,
          sublabel: null
        }, {
          key: 'pipeline' as MainTab,
          label: 'Pipeline',
          icon: GitBranch,
          sublabel: null
        }, {
          key: 'pullthrough' as MainTab,
          label: 'Metrics Tracker',
          icon: TrendingUp,
          sublabel: null
        }, {
          key: 'leaderboard' as MainTab,
          label: 'Leaderboard',
          icon: BarChart3,
          sublabel: null
        }, {
          key: 'commission' as MainTab,
          label: 'Commission Center',
          icon: Banknote,
          sublabel: isCommissionAuthed ? null : '🔒'
        }, {
          key: 'rewards' as MainTab,
          label: 'Reward Center',
          icon: Gift,
          sublabel: 'Coming Soon'
        }].map(({
          key,
          label,
          icon: Icon,
          sublabel
        }) => <motion.button key={key} onClick={() => setActiveTab(key)} whileHover={{
          y: -2
        }} whileTap={{
          scale: 0.97
        }} className={`relative flex items-center gap-2.5 px-5 py-3.5 text-sm font-semibold transition-all ${activeTab === key ? 'text-accent-400' : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.03]'} rounded-lg`} data-id="element-164">
              <Icon size={18} className={`transition-all duration-300 ${activeTab === key ? 'text-accent-400 drop-shadow-[0_0_6px_rgba(56,189,248,0.5)]' : ''}`} data-id="element-165" />
              <span data-id="element-166">{label}</span>
              {sublabel && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sublabel === 'Coming Soon' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/20' : sublabel === '🔒' ? 'text-amber-400' : 'bg-white/5 text-slate-500'}`} data-id="element-167">
                  {sublabel}
                </span>}
              {activeTab === key && <motion.div layoutId="activeTab" className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-accent-400 to-accent-500 rounded-full tab-glow" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-168" />}
            </motion.button>)}
        </div>
      </nav>

      {/* Filter Bar - Only show for Admin on Commission tab when authed */}
      {!isLoanOfficer && activeTab === 'commission' && isCommissionAuthed && <FilterBar role={currentRole} selectedProfiles={selectedProfiles} onProfileChange={setSelectedProfiles} dateFilter={dateFilter} onDateFilterChange={setDateFilter} data-id="element-169" />}

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto" data-id="element-170">
        <AnimatePresence mode="wait" data-id="element-171">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className={activeTab === 'commission' ? 'h-full' : undefined} data-id="element-172">
            {activeTab === 'rzone' && <RZoneView onNavigate={setActiveTab} data-id="element-173" />}
            {activeTab === 'pipeline' && <PipelineView data-id="element-174" />}
            {activeTab === 'pullthrough' && <PullThroughView data-id="element-175" />}
            {activeTab === 'leaderboard' && <LeaderboardView data-id="element-176" />}
            {activeTab === 'commission' && <>
                {showCommissionLock ? <PinEntry onSuccess={() => setIsCommissionAuthed(true)} data-id="element-177" /> : <>
                    {isLoanOfficer ? filteredCommissions.length > 0 ? <LoanOfficerView commission={filteredCommissions[0]} onApproveLoan={handleApproveLoan} onApproveAllLoans={handleApproveAllLoans} onFlagLoan={handleFlagLoan} onSubmitLoanResponse={handleSubmitLoanResponse} onApproveLoanResolution={handleApproveLoanResolution} data-id="element-178" /> : <div className="max-w-6xl mx-auto p-4" data-id="element-179">
                          <div className="bg-navy-800/70 rounded-xl border border-white/5 shadow-sm p-16 text-center" data-id="element-180">
                            <p className="text-slate-300 font-medium" data-id="element-181">
                              No commission data available
                            </p>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-182">
                              Your commission details will appear here once
                              loans are closed
                            </p>
                          </div>
                        </div> : viewingUserId ?
              // Admin impersonating a user — exact same view they see
              (() => {
                const viewedCommission = commissions.find(c => c.userId === viewingUserId);
                if (!viewedCommission) return null;
                return <div className="relative" data-id="element-183">
                            {/* Floating exit button */}
                            <motion.button initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} onClick={() => setViewingUserId(null)} className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold shadow-lg shadow-red-500/25 backdrop-blur-sm transition-all hover:scale-105" data-id="element-184">
                              <X size={16} data-id="element-185" />
                              <span data-id="element-186">
                                Exit {viewedCommission.userName}'s View
                              </span>
                            </motion.button>
                            <LoanOfficerView commission={viewedCommission} onApproveLoan={handleApproveLoan} onApproveAllLoans={handleApproveAllLoans} onFlagLoan={handleFlagLoan} onSubmitLoanResponse={handleSubmitLoanResponse} onApproveLoanResolution={handleApproveLoanResolution} data-id="element-187" />
                          </div>;
              })() : <div className="max-w-full mx-auto p-4" data-id="element-188">
                        <AdminSummaryMetrics data={filteredCommissions} onClickFlagged={() => setShowFlaggedPanel(true)} data-id="element-189" />
                        <div className="flex items-center gap-4 mb-6" data-id="element-190">
                          <div className="w-full max-w-md" data-id="element-191">
                            <SearchBar value={searchQuery} onChange={setSearchQuery} data-id="element-192" />
                          </div>
                        </div>
                        <AdminView commissions={filteredCommissions} onOpenFlaggedReview={() => setShowFlaggedPanel(true)} onViewUserCommission={userId => setViewingUserId(userId)} data-id="element-193" />
                      </div>}
                  </>}
              </>}
            {activeTab === 'rewards' && <ComingSoonView tab="rewards" data-id="element-194" />}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence data-id="element-195">
        {showFlaggedPanel && <FlaggedCommissionsPanel commissions={commissions} onClose={() => setShowFlaggedPanel(false)} onSubmitCounter={handleSubmitCounter} onSubmitResolution={handleSubmitResolution} data-id="element-196" />}
      </AnimatePresence>
    </div>;
}